import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Play, Calendar, Zap } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { WorkoutDetailModal } from "./WorkoutDetailModal";

const ProgramDetailModal = ({ program, workouts = [], onClose, onStartProgram, programProgress, nextUp }) => {
  const [activeWeek, setActiveWeek] = useState(nextUp?.weekIndex || 0);
  const [selectedDayWorkout, setSelectedDayWorkout] = useState(null);
  const [selectedDayContext, setSelectedDayContext] = useState(null);
  const [starting, setStarting] = useState(false);
  const nextUpRef = useRef(null);
  
  // Get workout details by ID
  const getWorkoutById = (workoutId) => {
    if (!workouts || !Array.isArray(workouts)) return null;
    return workouts.find(w => w.workout_id === workoutId) || null;
  };

  // Check if a day is completed
  const isDayCompleted = (weekNum, dayNum) => {
    if (!programProgress) return false;
    return programProgress.completed_days?.some(
      d => d.week === weekNum && d.day === dayNum
    );
  };

  // Calculate overall progress
  const totalDays = (program.weeks || []).reduce((acc, week) => acc + (week.days?.length || 0), 0);
  const completedDays = programProgress?.completed_days?.length || 0;
  const progressPercent = totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0;

  // Auto-scroll to the "Next Up" day card
  useEffect(() => {
    if (nextUpRef.current) {
      setTimeout(() => {
        nextUpRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [activeWeek]);

  const handleStartProgram = async () => {
    setStarting(true);
    try {
      const response = await fetch(`${API}/workouts/programs/${program.program_id}/start`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Program started! Let's go!");
        if (onStartProgram) onStartProgram();
      } else {
        toast.error("Failed to start program");
      }
    } catch (error) {
      console.error("Error starting program:", error);
      toast.error("Something went wrong");
    } finally {
      setStarting(false);
    }
  };

  const handleDayClick = (day, weekIndex, dayIndex) => {
    if (day.workout_id) {
      const workout = getWorkoutById(day.workout_id);
      if (workout) {
        setSelectedDayWorkout(workout);
        setSelectedDayContext({
          program_id: program.program_id,
          week: weekIndex + 1,
          day: dayIndex + 1
        });
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-surface border border-border rounded-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
      >
        {/* Header - Mobile Optimized */}
        <div className="sticky top-0 bg-surface border-b border-border p-4 sm:p-6 flex justify-between items-start gap-2 z-10">
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-xl sm:text-2xl font-bold truncate">{program.title}</h2>
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2">
              <span className="text-xs bg-success/20 text-success px-2 py-1 rounded capitalize">
                {(program.workout_type || program.goal)?.replace("_", " ")}
              </span>
              <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                {program.duration_weeks || program.weeks?.length || 0} weeks
              </span>
              {programProgress && (
                <span className="text-xs bg-accent/20 text-accent px-2 py-1 rounded">
                  {progressPercent}% complete
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Progress Bar */}
          {programProgress && (
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-text-secondary">Overall Progress</span>
                <span className="text-success font-medium">{completedDays}/{totalDays} days</span>
              </div>
              <div className="w-full bg-[#2D3B4F] rounded-full h-3">
                <div 
                  className="bg-success h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Description */}
          {program.description && (
            <p className="text-text-secondary mb-6">{program.description}</p>
          )}

          {/* Week Tabs - Scrollable on Mobile */}
          {program.weeks && program.weeks.length > 0 && (
            <>
              <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 pb-2 sm:pb-0 sm:flex-wrap">
                {program.weeks.map((week, index) => {
                  const weekDays = week.days?.length || 0;
                  const weekCompletedDays = (programProgress?.completed_days || []).filter(
                    d => d.week === index + 1
                  ).length;
                  const weekComplete = weekDays > 0 && weekCompletedDays >= weekDays;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => setActiveWeek(index)}
                      className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 ${
                        activeWeek === index 
                          ? "bg-success text-white" 
                          : weekComplete
                            ? "bg-success/20 text-success border border-success/30"
                            : nextUp && nextUp.weekIndex === index
                              ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              : "bg-[#1E293B] text-text-secondary hover:text-white"
                      }`}
                    >
                      {weekComplete && <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                      {!weekComplete && nextUp && nextUp.weekIndex === index && <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                      <span className="text-sm sm:text-base">Week {index + 1}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active Week Schedule - Mobile Optimized Grid */}
              <div className="bg-[#0B0F1A] border border-[#1E293B] rounded-xl p-3 sm:p-4">
                <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Week {activeWeek + 1} Schedule</h3>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                  {program.weeks[activeWeek]?.days?.map((day, dayIndex) => {
                    const workout = day.workout_id ? getWorkoutById(day.workout_id) : null;
                    const isComplete = isDayCompleted(activeWeek + 1, dayIndex + 1);
                    const isNextUp = nextUp && nextUp.weekIndex === activeWeek && nextUp.dayIndex === dayIndex;
                    
                    return (
                      <div 
                        key={dayIndex}
                        ref={isNextUp ? nextUpRef : null}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          isComplete
                            ? "bg-success/20 border border-success/50"
                            : isNextUp
                              ? "bg-amber-500/15 border-2 border-amber-400 ring-1 ring-amber-400/30"
                              : workout 
                                ? "bg-success/10 border border-success/30 hover:border-success/60" 
                                : "bg-[#141B2D] border border-[#1E293B]"
                        }`}
                        onClick={() => handleDayClick(day, activeWeek, dayIndex)}
                        data-testid={`program-day-${activeWeek}-${dayIndex}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-text-muted">Day {dayIndex + 1}</p>
                          {isComplete && <Check className="w-4 h-4 text-success" />}
                          {isNextUp && !isComplete && <Zap className="w-4 h-4 text-amber-400" />}
                        </div>
                        {workout ? (
                          <div>
                            <p className="font-medium text-sm">{workout.title}</p>
                            <p className="text-xs text-text-secondary mt-1">
                              {workout.estimated_duration || 45} min
                            </p>
                            {isNextUp && !isComplete && (
                              <div className="mt-2 text-xs font-semibold text-amber-300 flex items-center gap-1">
                                <Zap className="w-3 h-3" /> Next Up
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-text-muted">Rest Day</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Start Program Button */}
          <div className="mt-6 pt-6 border-t border-border">
            {!programProgress ? (
              <button
                onClick={handleStartProgram}
                disabled={starting}
                className="w-full bg-success text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {starting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Start Program
                  </>
                )}
              </button>
            ) : progressPercent < 100 ? (
              <div className="text-center text-text-secondary">
                <p>Click on a day to start that workout</p>
              </div>
            ) : (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-success mb-2">
                  <Check className="w-6 h-6" />
                  <span className="font-semibold text-lg">Program Complete!</span>
                </div>
                <p className="text-text-secondary">Congratulations on finishing this program!</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Nested Workout Modal */}
      <AnimatePresence>
        {selectedDayWorkout && (
          <WorkoutDetailModal
            workout={selectedDayWorkout}
            programContext={selectedDayContext}
            onClose={() => {
              setSelectedDayWorkout(null);
              setSelectedDayContext(null);
            }}
            onStart={() => {
              setSelectedDayWorkout(null);
              setSelectedDayContext(null);
              if (onStartProgram) onStartProgram();
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export { ProgramDetailModal };
